import {Component, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Observable, ReplaySubject} from 'rxjs';
import {Model} from '../../../../../domain/model/common/model.class';
import {ActivatedRoute} from '@angular/router';
import {FormErrors} from '../../../../../components/forms/validation/form-validator.class';
import {StepScreenModel} from '../../../../shared/step/step-screen.component';
import {FileLocation, FileLocationFactory} from '../../../../../components/files/service/file-location.class';
import {ProfessionalLicenseScreensManager} from './professional-license-screens-manager.service';
import {ProfessionalLicenseService} from '../../../../../domain/model/licenses-certifications/professional/professional-license.service';
import {ProfessionalLicense} from '../../../../../domain/model/licenses-certifications/professional/professional-license.model';
import {CountryService} from '../../../../../domain/model/info/country/country.service';
import {State} from '../../../../../domain/model/info/country/state.model';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import moment = require('moment');
import ProjectionInformation = Model.ProjectionInformation;
import { VerificationService } from '../../../../../components/verification/service/verification.service';

@Component({
    template: require('./professional-license-screen.component.html')
})
export class ProfessionalLicenseScreenComponent {

    constructor(readonly modelService: ProfessionalLicenseService,
                readonly route: ActivatedRoute,
                readonly screensManager: ProfessionalLicenseScreensManager,
                countryService: CountryService,
                readonly fileLocationFactory: FileLocationFactory,
                readonly verificationService: VerificationService) {

        //region Init State Objects
        this.states$ = countryService.getStatesForCountry('US').startWith([]);
        this.stateValues$ = this.states$.map(states => states.map(s => s.name));

        this.stepScreenModel.dataModel$.subscribe(model => {
            const state = model.state;
            this.initialState = state;
            this.stateCode$.next(state);
        });

        this.stateChange$.switchMap(this._toStateCode$).subscribe(this.stateCode$);
        this.stateCode$.subscribe(code => {
            this.model.state = code;
            this.stateChangeMessage = this.initialState !== code ? 'Since you selected a different state, any associated controlled substance permit information previously stored in the system (if any) will be removed.' : '';
        });
        this.stateName$ = this.stateCode$.switchMap(this._toStateName$);

        this.requiresClearanceDocuments$ = this.stateCode$.map(it => it === 'PA');
        this.requiresInfectiousControlTrainingCourse$ = this.stateCode$.map(it => it === 'NY');
        //endregion
    }

    // region Form
    readonly form$ = new ReplaySubject<NgForm>(1);

    @ViewChild(NgForm) set form(value: NgForm) {
        this.form$.next(value);
    }

    // endregion

    dateIssuedConstrains = {
        min: moment().subtract(100, 'year'),
        max: moment().add(5, 'year')
    };
    expirationDateConstrains = {
        min: moment().subtract(5, 'year'),
        max: moment().add(20, 'year'),
    };
    supportingDocumentFileLocation: FileLocation;
    childAbuseClearanceFileLocation: FileLocation;
    backgroundClearanceFileLocation: FileLocation;
    newYorkInfectiousControlTrainingCourseFileLocation: FileLocation;

    get index(): number {
        return +this.route.snapshot.params['index'];
    }

    stepScreenModel = new StepScreenModel<ProfessionalLicense>({
        initialDataModel: new ProfessionalLicense(),
        specificValidationMessages: {},
        screensManager: this.screensManager,
        _loadDataFn: () => {
            this.supportingDocumentFileLocation = this.fileLocationFactory.create('professionalLicense', `currentUser/${this.index}`, new ProjectionInformation('supportingDocument'));
            this.childAbuseClearanceFileLocation = this.fileLocationFactory.create('professionalLicense', `currentUser/${this.index}`, new ProjectionInformation('childAbuseClearance'));
            this.backgroundClearanceFileLocation = this.fileLocationFactory.create('professionalLicense', `currentUser/${this.index}`, new ProjectionInformation('backgroundClearance'));
            this.newYorkInfectiousControlTrainingCourseFileLocation = this.fileLocationFactory.create('professionalLicense', `currentUser/${this.index}`, new ProjectionInformation('newYorkInfectiousControlTrainingCourse'));
            this.verificationService.setUrl('professionalLicense', undefined, `${this.index}`);
            return this.modelService.getCurrentUserModelEntry(this.index);
        },
        _submitDataFn: (model: ProfessionalLicense) => {
            return this.modelService.updateCurrentUserModelEntry(this.index, model);
        },
        _hasYesNoQuestion: () => this.index !== 1 ? this.modelService.doesCurrentUserModelEntryExist(this.index).map(r => !r) : Observable.of(false)
    });

    get model(): ProfessionalLicense {
        return this.stepScreenModel.dataModel;
    }

    get formErrors(): FormErrors {
        return this.stepScreenModel.formErrors;
    }

    //region State
    private _toStateCode$ = (name: string): Observable<string | undefined> => {
        return this.states$.map(states => {
            const state = states.find(s => s.name === name);
            if (state) return state.code;
        });
    }

    private _toStateName$ = (code: string): Observable<string | undefined> => {
        return this.states$.map(states => {
            const state = states.find(s => s.code === code);
            if (state) return state.name;
        });
    }

    readonly states$: Observable<Array<State>>;
    readonly stateValues$: Observable<Array<string>>;
    readonly stateCode$ = new BehaviorSubject<string | undefined>(undefined);
    readonly stateName$: Observable<string> = this.stateCode$.switchMap(this._toStateName$);

    readonly stateChange$ = new ReplaySubject<string>(1);

    initialState?: string;
    stateChangeMessage: string = '';

    changeState(state: string) {
        this.stateChange$.next(state);
    }

    //endregion

    readonly requiresClearanceDocuments$: Observable<boolean>;
    readonly requiresInfectiousControlTrainingCourse$: Observable<boolean>;
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/licenses-certifications/steps/professional/professional-license/professional-license-screen.component.ts